import { DateTime } from "luxon";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { toFormat, calculateTimeRemaining, isBeforeToday } from "@/lib/dateHelpers";
import { Band, Person, EducationPgmProgram, PgmLevelStatus, ProgramStatus, ProgramTypeName } from "@/types";
import { isPgmUniversityGraduate } from "@/features/person/utils/pgm-university-metric";
import { isPatPassed } from "./pat-metric";

export const LEVEL_NUMBERS = [1, 2, 3] as const;
const PROGRESS_STATUS_VERSIONS = ["3.1"];

/** Path to Membership / shared progress helpers — Associate 3.1 only. */
export function isProgressVersionSupported(version?: string | null, programType?: string | null): boolean {
  if (!version || programType !== ProgramTypeName.Associate) return false;
  return PROGRESS_STATUS_VERSIONS.includes(version);
}

/** Person Overview PGM card — Associate/Affiliate/Student 3.1 (flagged surface only). */
export function isOverviewPgmProgressSupported(version?: string | null, programType?: string | null): boolean {
  if (!version || !programType) return false;
  if (
    programType !== ProgramTypeName.Associate &&
    programType !== ProgramTypeName.Affiliate &&
    programType !== ProgramTypeName.Student
  ) {
    return false;
  }
  return PROGRESS_STATUS_VERSIONS.includes(version);
}

const latestDate = (dates: string[]): string | null => [...dates].sort().reverse()[0] ?? null;

const QUALIFYING_LEVEL_DEADLINE_MONTHS = 9;
const ASSOCIATE_ELECTION_DEADLINE_MONTHS = 18;

const PROSPECT_BAND_REASONS = {
  PRE_QUALIFIER: "pre_qualifier",
  QUALIFIER_ELIGIBLE: "qualifier_eligible"
} as const;

function addMonthsToDateOnly(date: string, months: number): string | null {
  const parsed = DateTime.fromISO(date, { zone: "utc" });
  if (!parsed.isValid) return null;
  return parsed.plus({ months }).toISODate();
}

function dateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.split("T")[0] ?? null;
}

type QualifyingLevelAttempt = {
  startDate: string | null;
  completionDate: string | null;
  isCompleted: boolean;
  inProgress: boolean;
  isRestart: boolean;
};

function isQualifyingProgressComplete(status: string | null | undefined): boolean {
  return status === PGM_LEVEL_STATUS.COMPLETE || status === "completed";
}

function isQualifyingProgressInProgress(status: string | null | undefined): boolean {
  return isPgmLevelInProgress(status ?? "");
}

/**
 * Latest Qualifying Level attempt by start date. A newer in-progress F0 (or a
 * start date after the previous completion) is a restart — not still-complete.
 */
function getCurrentQualifyingLevelAttempt(person: Person): QualifyingLevelAttempt | null {
  const candidates: Array<{
    startDate: string | null;
    completionDate: string | null;
    isCompleted: boolean;
    inProgress: boolean;
  }> = [];

  for (const row of person.educationPgmProgramHistory ?? []) {
    if (row.level !== 0) continue;
    const isCompleted = !!row.completed;
    candidates.push({
      startDate: dateOnly(row.startDate),
      completionDate: dateOnly(row.endDate),
      isCompleted,
      inProgress: !isCompleted
    });
  }

  const qualifyingProgress = getQualifyingLevelProgress(person);
  if (qualifyingProgress) {
    const isCompleted = isQualifyingProgressComplete(qualifyingProgress.status) || !!qualifyingProgress.completionDate;
    candidates.push({
      startDate: dateOnly(qualifyingProgress.startDate),
      completionDate: dateOnly(qualifyingProgress.completionDate),
      isCompleted,
      inProgress: isQualifyingProgressInProgress(qualifyingProgress.status) || !isCompleted
    });
  }

  const qualifyingLevel = person.prospectProgram?.qualifyingLevel;
  if (qualifyingLevel) {
    const inProgressStatus = qualifyingLevel.status === "IN_PROGRESS";
    const isCompleted =
      !inProgressStatus &&
      (!!qualifyingLevel.completed || qualifyingLevel.status === "COMPLETED" || !!qualifyingLevel.endDate);
    if (qualifyingLevel.startDate || isCompleted || qualifyingLevel.status) {
      candidates.push({
        startDate: dateOnly(qualifyingLevel.startDate),
        completionDate: dateOnly(qualifyingLevel.endDate),
        isCompleted,
        inProgress: inProgressStatus || !isCompleted
      });
    }
  }

  const latestEducation = person.educationLatestPgmProgram;
  if (latestEducation?.level === 0) {
    const isCompleted = !!latestEducation.completed;
    candidates.push({
      startDate: dateOnly(latestEducation.startDate),
      completionDate: dateOnly(latestEducation.endDate),
      isCompleted,
      inProgress: !isCompleted
    });
  }

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    if (a.startDate !== b.startDate) {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return b.startDate.localeCompare(a.startDate);
    }
    if (a.inProgress !== b.inProgress) return a.inProgress ? -1 : 1;
    return 0;
  });

  const latest = candidates[0];
  const startAfterCompletion =
    !!latest.startDate && !!latest.completionDate && latest.startDate > latest.completionDate;
  const inProgress = latest.inProgress || startAfterCompletion;
  const isRestart = startAfterCompletion || candidates.slice(1).some((candidate) => candidate.isCompleted);

  return {
    startDate: latest.startDate,
    completionDate: startAfterCompletion ? null : latest.completionDate,
    isCompleted: latest.isCompleted && !inProgress,
    inProgress,
    isRestart
  };
}

function latestBandByStartDate(bands: Band[], createdReason: string): Band | undefined {
  return bands
    .filter((band) => band.createdReason === createdReason)
    .sort((a, b) => b.startedOn.localeCompare(a.startedOn))[0];
}

function getBandEndDate(band: Band): string | null {
  return band.terminatesOn ?? band.expiresOn ?? null;
}

function getQualifyingLevelProgress(person: Person): PgmLevelStatus | undefined {
  return person.pgmProgressStatus?.levels.find((level) => level.key === "qualifying");
}

export function isQualifyingLevelComplete(person: Person): boolean {
  const attempt = getCurrentQualifyingLevelAttempt(person);
  if (attempt?.inProgress) return false;
  if (attempt?.isCompleted) return true;

  if (person.pgmProgressStatus?.mostRecentCompletedLevelKey === "qualifying") return true;

  const qualifyingProgress = getQualifyingLevelProgress(person);
  if (qualifyingProgress?.status === PGM_LEVEL_STATUS.COMPLETE) return true;
  if (qualifyingProgress?.completionDate) return true;

  const qualifyingLevel = person.prospectProgram?.qualifyingLevel;
  if (qualifyingLevel?.completed || qualifyingLevel?.status === "COMPLETED" || qualifyingLevel?.endDate) {
    return true;
  }

  if (person.educationLatestPgmProgram?.level === 0 && person.educationLatestPgmProgram.completed) {
    return true;
  }

  return (person.educationPgmProgramHistory ?? []).some((item) => item.level === 0 && item.completed);
}

function isPreAssociateProspect(person: Person): boolean {
  const level1 = person.pgmProgressStatus?.levels.find((level) => level.numeral === 1);
  if (level1?.startDate) return false;

  const programTypeName = person.latestProgram?.programType?.name;
  if (programTypeName === ProgramTypeName.Prospect) return true;

  const bands = person.bands ?? [];
  return bands.some(
    (band) =>
      band.createdReason === PROSPECT_BAND_REASONS.PRE_QUALIFIER ||
      band.createdReason === PROSPECT_BAND_REASONS.QUALIFIER_ELIGIBLE
  );
}

const TERMINAL_APPLICATION_STATUSES = new Set(["denied", "rejected", "withdrawn", "cancelled"]);
/**
 * Member application statuses that satisfy the Associate summary gate.
 * Complete means approved or elected — not an in-flight submitted/under_review
 * application, and not `changes_requested` or draft/denied.
 */
const MEMBER_APPLICATION_COMPLETION_STATUSES = new Set(["approved", "elected", "complete"]);

/** True when an associate/affiliate election application is in flight or approved. */
function hasStartedAssociateOrAffiliateApplication(person: Person): boolean {
  const postgresApps = person.membershipApplications ?? [];
  const hasActivePostgresApp = postgresApps.some((application) => {
    const type = application.applicationType?.toLowerCase();
    if (type !== "associate" && type !== "affiliate") return false;

    const status = application.status?.toLowerCase();
    if (!status || status === "draft") return false;
    if (TERMINAL_APPLICATION_STATUSES.has(status)) return false;
    return true;
  });
  if (postgresApps.length > 0) return hasActivePostgresApp;

  const ccmsStatus = person.prospectProgram?.latestAssociateApplication?.status?.toLowerCase();
  if (!ccmsStatus) return false;
  return !TERMINAL_APPLICATION_STATUSES.has(ccmsStatus);
}

function isMemberApplicationCompleteStatus(status?: string | null): boolean {
  return MEMBER_APPLICATION_COMPLETION_STATUSES.has(status?.toLowerCase() ?? "");
}

/**
 * The progress payload maps an approved/elected Member application to
 * the Level 3 enrollment requirement across curriculum versions. History keeps
 * its coursework source, but the application status comes from this row.
 */
function hasCompletedMemberApplication(person: Person): boolean {
  const enrollment = person.pgmProgressStatus?.levels
    .find((level) => level.numeral === 3)
    ?.requirements.find((requirement) => requirement.key === "enrollment");

  return isMemberApplicationCompleteStatus(enrollment?.status);
}

function shouldShowAssociateApplicationDeadline(person: Person, expirationDate: string | null | undefined): boolean {
  if (!expirationDate || isBeforeToday(expirationDate)) return false;
  if (!isQualifyingLevelComplete(person)) return false;
  if (!isPreAssociateProspect(person)) return false;
  if (hasStartedAssociateOrAffiliateApplication(person)) return false;
  return true;
}

export const ASSOCIATE_APPLICATION_DEADLINE_LABEL = "Deadline for Associate Application";
export const QUALIFYING_LEVEL_DEADLINE_LABEL = "Deadline for QL Education";
export const PROGRAM_DEADLINE_LABEL = "Program Deadline";
export const PROGRAM_EXPIRED_LABEL = "Program Expired";
export const PROGRAM_START_DATE_LABEL = "Program Start Date";
export const PROGRAM_END_DATE_LABEL = "Program End Date";
export const PROGRAM_TIME_REMAINING_LABEL = "Program Time Remaining";
export const LEVEL_DEADLINE_LABEL = "Level Deadline";
export const LEVEL_EXPIRED_LABEL = "Level Expired";
export const LEVEL_CARD_DEADLINE_LABEL = "Deadline";
export const LEVEL_START_DATE_LABEL = "Level Start Date";
export const LEVEL_END_DATE_LABEL = "Level End Date";
export const LEVEL_TIME_REMAINING_LABEL = "Level Time Remaining";
export const PRIOR_PROGRAM_COMPLETION_TOOLTIP_TITLE = "Student Program";
export const PRIOR_PROGRAM_COMPLETION_TOOLTIP_TEXT = "Level completed in the Student Program.";

const DROPPED_PGM_STUDENT_REASON = "dropped_pgm_student";

export type ProspectDeadlineOptions = {
  /**
   * When true (`backoffice-ptm-qualifying-level-section`), the in-progress 9-month
   * clock uses Qualifying Level purchase/enrollment start. When false, uses the
   * legacy PQ band start (with startDate fallbacks).
   */
  useQualifyingLevelPurchaseStart?: boolean;
};

function pushAssociateApplicationDeadlineMetrics(
  metrics: MetricCardType<Path>[],
  expirationDate: string | null | undefined
): void {
  if (!expirationDate) return;

  metrics.push({
    label: ASSOCIATE_APPLICATION_DEADLINE_LABEL,
    value: toFormat(expirationDate),
    iconName: "schedule"
  });

  const timeRemaining = calculateTimeRemaining(expirationDate);
  if (timeRemaining) {
    metrics.push({
      label: PROGRAM_TIME_REMAINING_LABEL,
      value: timeRemaining,
      iconName: "schedule"
    });
  }
}

function prospectDeadlineLabel(person: Person): string {
  return isQualifyingLevelComplete(person) ? ASSOCIATE_APPLICATION_DEADLINE_LABEL : QUALIFYING_LEVEL_DEADLINE_LABEL;
}

function deadlineLabelForProgram(
  person: Person,
  programType: string | null | undefined,
  options: ProspectDeadlineOptions = {}
): string {
  if (programType === ProgramTypeName.Prospect && options.useQualifyingLevelPurchaseStart) {
    return prospectDeadlineLabel(person);
  }
  return PROGRAM_DEADLINE_LABEL;
}

export function hasStartedProspectProgram(person: Person): boolean {
  const qualifyingLevel = person.prospectProgram?.qualifyingLevel;
  const status = qualifyingLevel?.status;
  if (status === "NOT_STARTED") return false;

  if (
    status ||
    qualifyingLevel?.completed ||
    qualifyingLevel?.startDate ||
    qualifyingLevel?.endDate ||
    qualifyingLevel?.expirationDate
  ) {
    return true;
  }

  const programType = person.latestProgram?.programType?.name;
  if (programType && programType !== ProgramTypeName.Prospect) return true;

  return false;
}

function getQualifyingLevelPurchaseStartDate(person: Person): string | null {
  const currentAttempt = getCurrentQualifyingLevelAttempt(person);
  if (currentAttempt?.inProgress && currentAttempt.startDate) return currentAttempt.startDate;

  const qualifyingProgress = getQualifyingLevelProgress(person);
  if (qualifyingProgress?.startDate) return qualifyingProgress.startDate;

  const historyStart = (person.educationPgmProgramHistory ?? []).find((item) => item.level === 0)?.startDate;
  if (historyStart) return historyStart;

  return person.prospectProgram?.qualifyingLevel?.startDate ?? null;
}

/**
 * Prospect associate-path deadlines:
 * - While education is in progress: purchase start + 9 months when
 *   `useQualifyingLevelPurchaseStart` is on; otherwise PQ band start (+ legacy fallbacks)
 * - After education is complete: QE band expiration (or PQ/QE end + 18 months)
 */
export function getProspectAssociateProgramDeadline(
  person: Person,
  options: ProspectDeadlineOptions = {}
): string | null {
  const bands = person.bands ?? [];
  const pqBand = latestBandByStartDate(bands, PROSPECT_BAND_REASONS.PRE_QUALIFIER);
  const qeBand = latestBandByStartDate(bands, PROSPECT_BAND_REASONS.QUALIFIER_ELIGIBLE);
  const qualifyingProgress = getQualifyingLevelProgress(person);
  const qualifyingLevel = person.prospectProgram?.qualifyingLevel;

  if (isQualifyingLevelComplete(person)) {
    if (qeBand?.expiresOn) return qeBand.expiresOn;

    if (qeBand?.startedOn) return addMonthsToDateOnly(qeBand.startedOn, ASSOCIATE_ELECTION_DEADLINE_MONTHS);

    const pqEnd =
      (pqBand && getBandEndDate(pqBand)) ?? qualifyingLevel?.endDate ?? qualifyingProgress?.completionDate ?? null;
    if (pqEnd) return addMonthsToDateOnly(pqEnd, ASSOCIATE_ELECTION_DEADLINE_MONTHS);

    if (qualifyingLevel?.expirationDate) {
      return qualifyingLevel.expirationDate.split("T")[0];
    }

    return null;
  }

  if (options.useQualifyingLevelPurchaseStart) {
    const purchaseStart = getQualifyingLevelPurchaseStartDate(person);
    if (purchaseStart) return addMonthsToDateOnly(purchaseStart, QUALIFYING_LEVEL_DEADLINE_MONTHS);
    return null;
  }

  const pqStart = pqBand?.startedOn ?? qualifyingLevel?.startDate ?? qualifyingProgress?.startDate ?? null;
  if (pqStart) return addMonthsToDateOnly(pqStart, QUALIFYING_LEVEL_DEADLINE_MONTHS);

  return null;
}

export const PGM_LEVEL_STATUS = {
  LOCKED: "locked",
  COMPLETE: "complete",
  IN_PROGRESS: "in_progress"
} as const;

function isPgmLevelInProgress(status: string): boolean {
  return status === PGM_LEVEL_STATUS.IN_PROGRESS || status === "expired";
}

export function hasStartedPgmLevelOne(person: Person): boolean {
  const level1Progress = person.pgmProgressStatus?.levels.find((level) => level.numeral === 1);
  if (
    level1Progress?.startDate ||
    level1Progress?.completionDate ||
    level1Progress?.status === PGM_LEVEL_STATUS.IN_PROGRESS ||
    level1Progress?.status === PGM_LEVEL_STATUS.COMPLETE
  ) {
    return true;
  }

  if (person.educationLatestPgmProgram?.level === 1 && person.educationLatestPgmProgram.startDate) {
    return true;
  }

  return (person.educationPgmProgramHistory ?? []).some(
    (item) => item.level === 1 && (!!item.startDate || item.completed)
  );
}

export function getLastActivePgmLevelNumeral(person: Person): number | null {
  if (person.latestProgram?.programType?.name === ProgramTypeName.Member) {
    return null;
  }

  if (!hasStartedPgmLevelOne(person)) {
    return 0;
  }

  const { pgmProgressStatus, educationPgmProgramHistory = [] } = person;

  if (
    pgmProgressStatus &&
    isProgressVersionSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    const { currentLevelKey, levels } = pgmProgressStatus;
    const currentLevel =
      levels.find((level) => level.key === currentLevelKey) ??
      levels.find((level) => level.status === PGM_LEVEL_STATUS.IN_PROGRESS);

    if (currentLevel && isPgmLevelInProgress(currentLevel.status) && currentLevel.numeral > 0) {
      return currentLevel.numeral;
    }

    const inProgressLevel = [...levels]
      .filter(
        (level) => level.numeral > 0 && isPgmLevelInProgress(level.status) && (level.startDate || level.completionDate)
      )
      .sort((a, b) => b.numeral - a.numeral)[0];

    if (inProgressLevel) return inProgressLevel.numeral;

    if (currentLevel && currentLevel.status === PGM_LEVEL_STATUS.COMPLETE && currentLevel.numeral > 0) {
      return currentLevel.numeral;
    }

    const latestMeaningful = [...levels]
      .filter(
        (level) =>
          level.numeral > 0 && level.status !== PGM_LEVEL_STATUS.LOCKED && (level.startDate || level.completionDate)
      )
      .sort((a, b) => b.numeral - a.numeral)[0];

    return latestMeaningful?.numeral ?? null;
  }

  const inProgressHistoryLevel = [...educationPgmProgramHistory]
    .filter((item) => (item.level ?? 0) > 0 && !!item.startDate && !item.completed)
    .sort((a, b) => (b.level ?? 0) - (a.level ?? 0))[0];

  if (inProgressHistoryLevel) return inProgressHistoryLevel.level;

  const completedHistoryLevel = [...educationPgmProgramHistory]
    .filter((item) => (item.level ?? 0) > 0 && (item.completed || !!item.endDate))
    .sort((a, b) => (b.level ?? 0) - (a.level ?? 0))[0];

  return completedHistoryLevel?.level ?? null;
}

export type LevelMetricInput = {
  name: string;
  curriculumVersion?: string | null;
  startDate?: string | null;
  completionDate?: string | null;
  expirationDate?: string | null;
  isCompleted: boolean;
  programType?: string | null;
};

export function mapProgressToLevel(
  level: PgmLevelStatus | undefined,
  curriculumVersion?: string | null,
  programType?: string | null
): LevelMetricInput | null {
  if (!level || level.status === PGM_LEVEL_STATUS.LOCKED) return null;
  return {
    name: level.name,
    curriculumVersion,
    startDate: level.startDate,
    completionDate: level.completionDate,
    expirationDate: level.expirationDate,
    isCompleted: level.status === PGM_LEVEL_STATUS.COMPLETE,
    programType
  };
}

export function mapHistoryToLevel(
  label: string,
  levelNumber: number,
  levelHistory: EducationPgmProgram[]
): LevelMetricInput | null {
  const levelData = levelHistory.find((item) => item.level === levelNumber);
  if (!levelData) return null;
  return {
    name: label,
    curriculumVersion: levelData.curriculumVersion,
    startDate: levelData.startDate,
    completionDate: levelData.endDate,
    expirationDate: levelData.levelExpirationDate,
    isCompleted: !!levelData.completed,
    programType: levelData.programType
  };
}

function hasDroppedPgmStudentBand(person: Person): boolean {
  return (person.bands ?? []).some(
    (band) => band.programType?.name === ProgramTypeName.Student && band.terminatedReason === DROPPED_PGM_STUDENT_REASON
  );
}

/**
 * Student graduation for PTM labels and whole-program completion.
 * A drop is never a graduate, even when `pgmProgramCompleted` is stale.
 * Otherwise `isPgmUniversityGraduate` (`pgmProgramCompleted` or `pgm_graduate`).
 * Associate membership with a Student-typed latest row and `pgmProgramCompleted`
 * (and no drop) is treated as graduated — that flag is the completion signal.
 */
function isStudentProgramGraduate(person: Person): boolean {
  if (hasDroppedPgmStudentBand(person)) return false;
  return isPgmUniversityGraduate(person);
}

/**
 * Prior-path typing for a level: prefer a completed Associate row when dual-typed
 * history exists so At a Glance does not show a Student tooltip for Associate work.
 * Otherwise prefer completed Student, then any typed row.
 */
function getEducationProgramTypeForLevel(person: Person, levelNumber: number): string | null {
  const matches = (person.educationPgmProgramHistory ?? []).filter((item) => item.level === levelNumber);
  const completedAssociate = matches.find((item) => item.completed && item.programType === ProgramTypeName.Associate);
  if (completedAssociate) return ProgramTypeName.Associate;
  const completedStudent = matches.find((item) => item.completed && item.programType === ProgramTypeName.Student);
  if (completedStudent) return ProgramTypeName.Student;
  return matches.find((item) => item.programType)?.programType ?? null;
}

function withHistoryProgramType(
  level: LevelMetricInput,
  person: Person,
  levelNumber: number,
  fallbackProgramType?: string | null
): LevelMetricInput {
  return {
    ...level,
    programType: getEducationProgramTypeForLevel(person, levelNumber) ?? fallbackProgramType ?? level.programType
  };
}

export function isPgmEducationProgramComplete(person: Person): boolean {
  // Graduate detection (including non-dropped `pgmProgramCompleted`) lives in
  // `isStudentProgramGraduate` — do not re-check `pgmProgramCompleted` here.
  if (isStudentProgramGraduate(person)) return true;

  const currentLevelNumber = getProgramLevelNumber(person);
  if (currentLevelNumber !== 3) return false;

  return getLevelInput(person, 3)?.isCompleted === true;
}

function summaryProgramTypeForMembership(person: Person, educationProgramType: string | null): string | null {
  if (person.latestProgram?.programType?.name === ProgramTypeName.Associate) {
    return ProgramTypeName.Associate;
  }
  return educationProgramType;
}

export function getPriorProgramCompletionTooltip(
  person: Person,
  level: LevelMetricInput | null
): MetricCardType<Path>["tooltip"] | null {
  if (!level?.isCompleted) return null;
  if (person.latestProgram?.programType?.name !== ProgramTypeName.Associate) return null;
  if (isStudentProgramGraduate(person)) return null;
  if (level.programType !== ProgramTypeName.Student) return null;

  return {
    title: PRIOR_PROGRAM_COMPLETION_TOOLTIP_TITLE,
    supportingText: PRIOR_PROGRAM_COMPLETION_TOOLTIP_TEXT
  };
}

const QUALIFYING_LEVEL_VALUE = "Qualifying Level";

export function getQualifyingLevelMetricInput(person: Person): LevelMetricInput | null {
  const { pgmProgressStatus, educationPgmProgramHistory = [], prospectProgram } = person;
  const currentAttempt = getCurrentQualifyingLevelAttempt(person);

  if (
    pgmProgressStatus &&
    isProgressVersionSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    const level = pgmProgressStatus.levels.find((item) => item.numeral === 0 || item.key === "qualifying");
    const fromProgress = mapProgressToLevel(level, pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType);
    if (fromProgress) {
      return withHistoryProgramType(
        applyCurrentQualifyingAttempt(fromProgress, currentAttempt),
        person,
        0,
        pgmProgressStatus.programType
      );
    }
  }

  const historyLevel = mapHistoryToLevel(QUALIFYING_LEVEL_VALUE, 0, educationPgmProgramHistory);
  if (historyLevel) {
    return withHistoryProgramType(applyCurrentQualifyingAttempt(historyLevel, currentAttempt), person, 0);
  }

  const qualifyingLevel = prospectProgram?.qualifyingLevel;
  if (qualifyingLevel) {
    const isCompleted =
      !!qualifyingLevel.completed || qualifyingLevel.status === "COMPLETED" || !!qualifyingLevel.endDate;

    if (qualifyingLevel.startDate || isCompleted || qualifyingLevel.expirationDate) {
      return withHistoryProgramType(
        applyCurrentQualifyingAttempt(
          {
            name: QUALIFYING_LEVEL_VALUE,
            curriculumVersion: null,
            startDate: qualifyingLevel.startDate,
            completionDate: qualifyingLevel.endDate,
            expirationDate: qualifyingLevel.expirationDate,
            isCompleted
          },
          currentAttempt
        ),
        person,
        0
      );
    }
  }

  if (isQualifyingLevelComplete(person)) {
    return withHistoryProgramType(
      {
        name: QUALIFYING_LEVEL_VALUE,
        curriculumVersion: null,
        startDate: null,
        completionDate: null,
        expirationDate: null,
        isCompleted: true
      },
      person,
      0
    );
  }

  return null;
}

function applyCurrentQualifyingAttempt(
  level: LevelMetricInput,
  attempt: QualifyingLevelAttempt | null
): LevelMetricInput {
  if (!attempt?.inProgress) return level;

  const startDate = attempt.startDate ?? level.startDate;
  const currentExpiration = dateOnly(level.expirationDate);
  const leftoverExpirationIsStale = !!startDate && !!currentExpiration && currentExpiration < startDate;
  const expirationDate =
    startDate && (attempt.isRestart || leftoverExpirationIsStale)
      ? addMonthsToDateOnly(startDate, QUALIFYING_LEVEL_DEADLINE_MONTHS)
      : level.expirationDate;

  return {
    ...level,
    startDate,
    completionDate: null,
    expirationDate,
    isCompleted: false
  };
}

function isExpiredLevel(level: LevelMetricInput): boolean {
  return !!level.startDate && !level.isCompleted && isBeforeToday(level.expirationDate);
}

export function isPgmProgressLevelExpired(level: {
  status: string;
  startDate?: string | null;
  expirationDate?: string | null;
}): boolean {
  if (level.status === "expired") return true;
  if (level.status === "complete") return false;

  return !!level.startDate && isBeforeToday(level.expirationDate);
}

function notStartedMetric(value: string): MetricCardType<Path> {
  return { label: "Not Started", value, iconName: "lock", variant: undefined };
}

export function buildLevelMetric(
  level: LevelMetricInput | null,
  fallbackName: string,
  isSuspended: boolean
): MetricCardType<Path> {
  if (!level) {
    return notStartedMetric(fallbackName);
  }

  const labelParts: string[] = [];
  let variant: MetricCardType<Path>["variant"];
  let tooltip: MetricCardType<Path>["tooltip"] = null;

  if (level.curriculumVersion) {
    labelParts.push(`Version: ${level.curriculumVersion}`);
  }

  if (level.startDate) {
    labelParts.push(`Started: ${toFormat(level.startDate)}`);
  }

  if (level.isCompleted) {
    variant = "success";
    if (level.completionDate) {
      labelParts.push(`Completed: ${toFormat(level.completionDate)}`);
    } else if (labelParts.length === 0) {
      labelParts.push("Completed");
    }
  } else if (isExpiredLevel(level) && isSuspended) {
    variant = "warn";
    if (level.expirationDate) {
      labelParts.push(`Suspended: ${toFormat(level.expirationDate)}`);
    }
  } else if (isExpiredLevel(level)) {
    variant = "error";
    if (level.expirationDate) {
      labelParts.push(`Expired: ${toFormat(level.expirationDate)}`);
    }
  } else if (level.startDate) {
    variant = "info";
    if (level.expirationDate) {
      labelParts.push(`${LEVEL_CARD_DEADLINE_LABEL}: ${toFormat(level.expirationDate)}`);
      const timeRemaining = calculateTimeRemaining(level.expirationDate);
      if (timeRemaining) {
        tooltip = { title: LEVEL_TIME_REMAINING_LABEL, supportingText: `${timeRemaining}` };
      }
    }
  } else {
    return notStartedMetric(level.name);
  }

  return {
    label: labelParts.length > 0 ? labelParts.join("\n") : "Unknown",
    value: level.name,
    iconName: "school",
    variant,
    tooltip
  };
}

function getLevelInput(person: Person, levelNumber: number): LevelMetricInput | null {
  if (levelNumber === 0) return getQualifyingLevelMetricInput(person);

  const { pgmProgressStatus, educationPgmProgramHistory = [] } = person;

  if (
    pgmProgressStatus &&
    isProgressVersionSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    const level = pgmProgressStatus.levels.find((l) => l.numeral === levelNumber);
    const fromProgress = mapProgressToLevel(level, pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType);
    return fromProgress
      ? withHistoryProgramType(fromProgress, person, levelNumber, pgmProgressStatus.programType)
      : null;
  }

  const fromHistory = mapHistoryToLevel(`Level ${levelNumber}`, levelNumber, educationPgmProgramHistory);
  return fromHistory ? withHistoryProgramType(fromHistory, person, levelNumber) : null;
}

type ProgramDetailsInput = {
  programType: string;
  curriculumVersion?: string | null;
  careerPath?: string | null;
  startDate?: string | null;
  completionDate?: string | null;
  expirationDate?: string | null;
  isCompleted: boolean;
};

function getLevel1StartDate(person: Person): string | null {
  const { pgmProgressStatus, educationPgmProgramHistory = [] } = person;

  if (
    pgmProgressStatus &&
    isProgressVersionSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    return pgmProgressStatus.levels.find((level) => level.numeral === 1)?.startDate ?? null;
  }

  return educationPgmProgramHistory.find((item) => item.level === 1)?.startDate ?? null;
}

function getProgramDetailsInput(person: Person, options: ProspectDeadlineOptions = {}): ProgramDetailsInput | null {
  const { pgmProgressStatus, educationPgmProgramHistory = [] } = person;

  if (
    pgmProgressStatus &&
    isProgressVersionSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    const { programType, careerPath, curriculumVersion, programExpirationDate, levels } = pgmProgressStatus;
    const completionDates = levels.map((l) => l.completionDate).filter(Boolean) as string[];

    let expirationDate = programExpirationDate ?? null;
    if (isPreAssociateProspect(person)) {
      const prospectDeadline = getProspectAssociateProgramDeadline(person, options);
      if (prospectDeadline) expirationDate = prospectDeadline;
    }

    return {
      programType: summaryProgramTypeForMembership(person, programType || "PGM") || "PGM",
      curriculumVersion,
      careerPath,
      startDate: getLevel1StartDate(person),
      completionDate: latestDate(completionDates),
      expirationDate,
      isCompleted: isPgmEducationProgramComplete(person)
    };
  }

  if (educationPgmProgramHistory.length === 0) return null;

  const first = educationPgmProgramHistory[0];
  const endDates = educationPgmProgramHistory.map((h) => h.endDate).filter(Boolean) as string[];
  const expirationDates = educationPgmProgramHistory.map((h) => h.pgmProgramExpirationDate).filter(Boolean) as string[];

  return {
    programType: summaryProgramTypeForMembership(person, first.programType || "PGM") || "PGM",
    curriculumVersion: first.curriculumVersion,
    careerPath: first.careerPath,
    startDate: getLevel1StartDate(person),
    completionDate: latestDate(endDates),
    expirationDate: latestDate(expirationDates),
    isCompleted: isPgmEducationProgramComplete(person)
  };
}

export interface ProgramDetailsSectionData {
  heading: string;
  body: string | null;
  dateMetrics: MetricCardType<Path>[];
}

export function buildProgramDetailsSectionData(
  person: Person,
  options: ProspectDeadlineOptions = {}
): ProgramDetailsSectionData {
  const input = getProgramDetailsInput(person, options);

  if (!input) {
    return {
      heading: "Program Details",
      body: null,
      dateMetrics: [
        { label: PROGRAM_START_DATE_LABEL, value: "Not Started", iconName: "today" },
        { label: PROGRAM_END_DATE_LABEL, value: null, iconName: "today" }
      ]
    };
  }

  const heading = `${input.programType} Program`;
  const body = input.curriculumVersion ? `Version: ${input.curriculumVersion}` : null;

  const metrics: MetricCardType<Path>[] = [];

  metrics.push({
    label: PROGRAM_START_DATE_LABEL,
    value: input.startDate ? toFormat(input.startDate) : "Not Started",
    iconName: "today"
  });

  if (input.isCompleted) {
    metrics.push({
      label: PROGRAM_END_DATE_LABEL,
      value: input.completionDate ? toFormat(input.completionDate) : null,
      iconName: "today"
    });
  } else if (isBeforeToday(input.expirationDate)) {
    metrics.push({
      label: PROGRAM_EXPIRED_LABEL,
      value: input.expirationDate ? toFormat(input.expirationDate) : null,
      iconName: "schedule"
    });
  } else if (input.startDate || input.expirationDate) {
    metrics.push({
      label: deadlineLabelForProgram(person, input.programType, options),
      value: input.expirationDate ? toFormat(input.expirationDate) : null,
      iconName: "schedule"
    });

    const timeRemaining = input.expirationDate ? calculateTimeRemaining(input.expirationDate) : null;
    if (timeRemaining) {
      metrics.push({
        label: PROGRAM_TIME_REMAINING_LABEL,
        value: timeRemaining,
        iconName: "schedule"
      });
    }
  } else {
    metrics.push({ label: PROGRAM_END_DATE_LABEL, value: null, iconName: "today" });
  }

  if (input.careerPath) {
    metrics.push({
      label: "Career Path",
      value: input.careerPath,
      iconName: "school"
    });
  }

  return { heading, body, dateMetrics: metrics };
}

type ProgramSummaryStatus = "In Progress" | "Completed" | "Expired" | "Suspended" | "Terminated";

type ProgramSummaryInput = {
  programType: string;
  curriculumVersion?: string | null;
  careerPath?: string | null;
  startDate?: string | null;
  completionDate?: string | null;
  expirationDate?: string | null;
  isCompleted: boolean;
  status: ProgramSummaryStatus;
};

function getHistoryProgramLevelNumber(person: Person): number | null {
  const history = person.educationPgmProgramHistory ?? [];
  const inProgress = [...history]
    .filter((item) => (item.level ?? 0) > 0 && !!item.startDate && !item.completed)
    .sort((a, b) => (b.level ?? 0) - (a.level ?? 0))[0];

  if (inProgress) return inProgress.level;

  const completed = [...history]
    .filter((item) => (item.level ?? 0) > 0 && (item.completed || !!item.endDate))
    .sort((a, b) => (b.level ?? 0) - (a.level ?? 0))[0];

  return completed?.level ?? null;
}

function getProgressProgramLevelNumber(person: Person): number | null {
  const progress = person.pgmProgressStatus;
  if (!progress || !isProgressVersionSupported(progress.curriculumVersion, progress.programType)) {
    return null;
  }

  const levels = progress.levels.filter((level) => level.numeral > 0);
  const current = levels.find((level) => level.key === progress.currentLevelKey);
  if (current && isPgmLevelInProgress(current.status)) return current.numeral;

  const inProgress = [...levels]
    .filter((level) => isPgmLevelInProgress(level.status) && !!level.startDate)
    .sort((a, b) => b.numeral - a.numeral)[0];

  if (inProgress) return inProgress.numeral;
  if (current?.status === PGM_LEVEL_STATUS.COMPLETE) return current.numeral;

  const completed = [...levels]
    .filter((level) => level.status === PGM_LEVEL_STATUS.COMPLETE || !!level.completionDate)
    .sort((a, b) => b.numeral - a.numeral)[0];

  return completed?.numeral ?? null;
}

function getProgramLevelNumber(person: Person): number | null {
  return getProgressProgramLevelNumber(person) ?? getHistoryProgramLevelNumber(person);
}

function getProspectProgramSummaryInput(
  person: Person,
  options: ProspectDeadlineOptions = {}
): ProgramSummaryInput | null {
  const qualifyingLevel = getQualifyingLevelMetricInput(person);
  if (!qualifyingLevel) return null;

  const expirationDate = getProspectAssociateProgramDeadline(person, options) ?? qualifyingLevel.expirationDate ?? null;

  return {
    programType: ProgramTypeName.Prospect,
    curriculumVersion: null,
    careerPath: null,
    startDate: qualifyingLevel.startDate,
    completionDate: qualifyingLevel.completionDate,
    expirationDate,
    isCompleted: qualifyingLevel.isCompleted,
    status: getProgramStatus(
      person,
      ProgramTypeName.Prospect,
      qualifyingLevel,
      expirationDate,
      qualifyingLevel.isCompleted
    )
  };
}

function getProgramTypeForLevel(person: Person, levelNumber: number): string | null {
  const progress = person.pgmProgressStatus;
  if (
    progress &&
    isProgressVersionSupported(progress.curriculumVersion, progress.programType) &&
    progress.programType
  ) {
    return progress.programType;
  }

  const historyLevel = person.educationPgmProgramHistory?.find((item) => item.level === levelNumber);
  return (
    historyLevel?.programType ??
    person.educationPgmProgramHistory?.find((item) => item.programType)?.programType ??
    person.latestProgram?.programType?.name ??
    null
  );
}

function getProgramStartDate(person: Person): string | null {
  const progress = person.pgmProgressStatus;
  if (progress && isProgressVersionSupported(progress.curriculumVersion, progress.programType)) {
    const progressDates = progress.levels
      .filter((level) => level.numeral > 0)
      .map((level) => level.startDate)
      .filter(Boolean) as string[];

    return progressDates.sort()[0] ?? null;
  }

  const historyDates = (person.educationPgmProgramHistory ?? [])
    .filter((item) => (item.level ?? 0) > 0)
    .map((item) => item.startDate)
    .filter(Boolean) as string[];

  return historyDates.sort()[0] ?? null;
}

function getProgramCompletionDate(person: Person): string | null {
  const progress = person.pgmProgressStatus;
  if (progress && isProgressVersionSupported(progress.curriculumVersion, progress.programType)) {
    const progressDates = progress.levels
      .filter((level) => level.numeral > 0)
      .map((level) => level.completionDate)
      .filter(Boolean) as string[];

    return latestDate(progressDates);
  }

  const historyDates = (person.educationPgmProgramHistory ?? [])
    .filter((item) => (item.level ?? 0) > 0)
    .map((item) => item.endDate)
    .filter(Boolean) as string[];

  return latestDate(historyDates);
}

function getProgramExpirationDate(person: Person, options: ProspectDeadlineOptions = {}): string | null {
  const { pgmProgressStatus, educationPgmProgramHistory = [] } = person;

  if (
    pgmProgressStatus &&
    isProgressVersionSupported(pgmProgressStatus.curriculumVersion, pgmProgressStatus.programType)
  ) {
    let expirationDate = pgmProgressStatus.programExpirationDate ?? null;
    if (isPreAssociateProspect(person)) {
      const prospectDeadline = getProspectAssociateProgramDeadline(person, options);
      if (prospectDeadline) expirationDate = prospectDeadline;
    }
    return expirationDate;
  }

  return latestDate(
    educationPgmProgramHistory
      .map((history) => history.pgmProgramExpirationDate ?? history.levelExpirationDate)
      .filter(Boolean) as string[]
  );
}

function getProgramCareerPath(person: Person, levelNumber: number): string | null {
  const progress = person.pgmProgressStatus;
  if (progress && isProgressVersionSupported(progress.curriculumVersion, progress.programType) && progress.careerPath) {
    return progress.careerPath;
  }

  return (
    person.educationPgmProgramHistory?.find((item) => item.level === levelNumber)?.careerPath ??
    person.educationPgmProgramHistory?.find((item) => item.careerPath)?.careerPath ??
    null
  );
}

function membershipStatusAppliesToSummary(person: Person, summaryProgramType: string): boolean {
  const membershipType = person.latestProgram?.programType?.name;
  return (
    membershipType === summaryProgramType ||
    (membershipType === ProgramTypeName.Affiliate && summaryProgramType === ProgramTypeName.Associate)
  );
}

function isProgramSummaryComplete(person: Person, programType: string, isEducationComplete: boolean): boolean {
  if (!isEducationComplete) return false;
  if (programType !== ProgramTypeName.Associate) return true;
  if (person.latestProgram?.programType?.name !== ProgramTypeName.Associate) return true;

  return (
    person.experienceCredits?.complete === true &&
    isPatPassed(person.prospectProgram?.playingAbilityTestStatus, {
      pathAwareExpiration: true,
      person
    }) &&
    hasCompletedMemberApplication(person)
  );
}

function getProgramStatus(
  person: Person,
  programType: string,
  level: LevelMetricInput,
  expirationDate: string | null,
  isSummaryComplete: boolean
): ProgramSummaryStatus {
  const latestProgramMatchesSummary = membershipStatusAppliesToSummary(person, programType);

  if (latestProgramMatchesSummary && person.latestProgram?.status === ProgramStatus.SUSPENDED) return "Suspended";
  if (latestProgramMatchesSummary && person.latestProgram?.status === ProgramStatus.TERMINATED) {
    if (isBeforeToday(expirationDate)) return "Expired";
    return "Terminated";
  }
  if (!isSummaryComplete && (isExpiredLevel(level) || isBeforeToday(expirationDate))) return "Expired";
  return isSummaryComplete ? "Completed" : "In Progress";
}

function getProgramSummaryInput(person: Person, options: ProspectDeadlineOptions = {}): ProgramSummaryInput | null {
  if (person.latestProgram?.programType?.name === ProgramTypeName.Prospect) {
    return getProspectProgramSummaryInput(person, options);
  }

  const levelNumber = getProgramLevelNumber(person);
  if (levelNumber === null) return null;

  const level = getLevelInput(person, levelNumber);
  const educationProgramType = getProgramTypeForLevel(person, levelNumber);
  const programType = summaryProgramTypeForMembership(person, educationProgramType);
  if (!level || !programType) return null;

  const expirationDate = getProgramExpirationDate(person, options) ?? level.expirationDate ?? null;
  const isEducationComplete = isPgmEducationProgramComplete(person);
  const isSummaryComplete = isProgramSummaryComplete(person, programType, isEducationComplete);

  return {
    programType,
    curriculumVersion: level.curriculumVersion,
    careerPath: getProgramCareerPath(person, levelNumber),
    startDate: getProgramStartDate(person),
    completionDate: getProgramCompletionDate(person),
    expirationDate,
    isCompleted: isEducationComplete,
    status: getProgramStatus(person, programType, level, expirationDate, isSummaryComplete)
  };
}

export const ACCEPTABLE_PROGRESS_TERMINATION_REASON = "terminated_failure_to_meet_acceptable_progress";

export function shouldShowAcceptableProgressTerminationBanner(
  person: Person,
  options: ProspectDeadlineOptions = {}
): boolean {
  if (person.latestProgram?.status !== ProgramStatus.TERMINATED) return false;
  if (person.latestProgram.band?.terminatedReason !== ACCEPTABLE_PROGRESS_TERMINATION_REASON) return false;
  return getProgramSummaryInput(person, options)?.status === "Terminated";
}

export function buildLevelMetrics(person: Person, isSuspended: boolean): MetricCardType<Path>[] {
  return LEVEL_NUMBERS.map((n) => {
    const level = getLevelInput(person, n);
    const metric = buildLevelMetric(level, level?.name ?? `Level ${n}`, isSuspended);
    const priorProgramTooltip = getPriorProgramCompletionTooltip(person, level);
    if (!priorProgramTooltip) return metric;
    return { ...metric, tooltip: priorProgramTooltip };
  });
}

export interface ProgramSummarySectionData {
  heading: string;
  metrics: MetricCardType<Path>[];
}

export function buildProgramSummarySectionData(
  person: Person,
  options: ProspectDeadlineOptions = {}
): ProgramSummarySectionData {
  const input = getProgramSummaryInput(person, options);

  if (!input) {
    return {
      heading: "Path to Membership",
      metrics: []
    };
  }

  const metrics: MetricCardType<Path>[] = [
    {
      label: PROGRAM_START_DATE_LABEL,
      value: input.startDate ? toFormat(input.startDate) : null,
      iconName: "today"
    }
  ];

  const showEducationCompletionMetrics = input.isCompleted && input.programType === ProgramTypeName.Prospect;
  if (input.status === "Completed" || showEducationCompletionMetrics) {
    metrics.push({
      label: PROGRAM_END_DATE_LABEL,
      value: input.completionDate ? toFormat(input.completionDate) : null,
      iconName: "today"
    });

    if (
      input.programType === ProgramTypeName.Prospect &&
      shouldShowAssociateApplicationDeadline(person, input.expirationDate)
    ) {
      pushAssociateApplicationDeadlineMetrics(metrics, input.expirationDate);
    } else if (input.status === "Expired" && input.expirationDate) {
      metrics.push({
        label: PROGRAM_EXPIRED_LABEL,
        value: toFormat(input.expirationDate),
        iconName: "schedule"
      });
    }
  } else if (input.status === "Suspended" || input.status === "Terminated") {
    metrics.push({
      label: deadlineLabelForProgram(person, input.programType, options),
      value: input.expirationDate ? toFormat(input.expirationDate) : null,
      iconName: "schedule"
    });

    if (input.expirationDate) {
      const timeRemaining = calculateTimeRemaining(input.expirationDate);
      if (timeRemaining) {
        metrics.push({
          label: PROGRAM_TIME_REMAINING_LABEL,
          value: timeRemaining,
          iconName: "schedule"
        });
      }
    }
  } else if (input.status === "Expired") {
    metrics.push({
      label: PROGRAM_EXPIRED_LABEL,
      value: input.expirationDate ? toFormat(input.expirationDate) : null,
      iconName: "schedule"
    });
  } else if (input.expirationDate) {
    metrics.push({
      label: deadlineLabelForProgram(person, input.programType, options),
      value: toFormat(input.expirationDate),
      iconName: "schedule"
    });

    const timeRemaining = calculateTimeRemaining(input.expirationDate);
    if (timeRemaining) {
      metrics.push({
        label: PROGRAM_TIME_REMAINING_LABEL,
        value: timeRemaining,
        iconName: "schedule"
      });
    }
  } else {
    metrics.push({ label: PROGRAM_END_DATE_LABEL, value: null, iconName: "today" });
  }

  if (
    input.status === "Completed" &&
    (input.programType === ProgramTypeName.Associate || input.programType === ProgramTypeName.Student) &&
    input.curriculumVersion
  ) {
    metrics.push({
      label: "Curriculum Version",
      value: input.curriculumVersion,
      iconName: "school"
    });
  }

  if (input.careerPath) {
    metrics.push({
      label: "Career Path",
      value: input.careerPath,
      iconName: "school"
    });
  }

  if (input.programType === ProgramTypeName.Student) {
    const universityName = person.university?.name ?? person.lastAttendedPgmUniversity?.name;
    if (universityName) {
      metrics.push({
        label: "PGM University",
        value: universityName,
        iconName: "school"
      });
    }
  }

  return {
    heading: `${input.programType} Program (${input.status})`,
    metrics
  };
}

function buildStartDateMetric(level: LevelMetricInput | null): MetricCardType<Path> {
  if (level?.startDate) {
    return { label: LEVEL_START_DATE_LABEL, value: toFormat(level.startDate), iconName: "today" };
  }

  if (level?.isCompleted) {
    return { label: LEVEL_START_DATE_LABEL, value: null, iconName: "today" };
  }

  return {
    label: LEVEL_START_DATE_LABEL,
    value: "Not Started",
    iconName: "today"
  };
}

function buildEndDateMetric(
  level: LevelMetricInput | null,
  isSuspended: boolean,
  suspensionReason?: string | null
): MetricCardType<Path> {
  if (!level) {
    return { label: LEVEL_END_DATE_LABEL, value: null, iconName: "today" };
  }

  if (level.isCompleted) {
    return {
      label: LEVEL_END_DATE_LABEL,
      value: level.completionDate ? toFormat(level.completionDate) : null,
      iconName: "today"
    };
  }

  if (!level.startDate) {
    return { label: LEVEL_END_DATE_LABEL, value: null, iconName: "today" };
  }

  if (isExpiredLevel(level)) {
    return {
      label: isSuspended ? "Suspended" : LEVEL_EXPIRED_LABEL,
      value: level.expirationDate ? toFormat(level.expirationDate) : null,
      iconName: "schedule",
      ...(isSuspended && suspensionReason ? { tooltip: { supportingText: suspensionReason } } : {})
    };
  }

  return {
    label: LEVEL_DEADLINE_LABEL,
    value: level.expirationDate ? toFormat(level.expirationDate) : null,
    iconName: "schedule"
  };
}

function buildTimeRemainingMetric(level: LevelMetricInput | null): MetricCardType<Path> | null {
  if (!level || !level.startDate || level.isCompleted || isExpiredLevel(level)) return null;
  const timeRemaining = level.expirationDate ? calculateTimeRemaining(level.expirationDate) : null;
  if (!timeRemaining) return null;
  return {
    label: LEVEL_TIME_REMAINING_LABEL,
    value: timeRemaining,
    iconName: "schedule"
  };
}

function buildTerminationMetric(
  level: LevelMetricInput | null,
  isSuspended: boolean,
  terminationTimeRemaining?: string | null
): MetricCardType<Path> | null {
  if (!level || !isExpiredLevel(level) || !isSuspended || !terminationTimeRemaining) return null;
  return {
    label: "Time Until Termination",
    value: terminationTimeRemaining,
    iconName: "schedule"
  };
}

interface LevelDateMetricsOptions {
  isSuspended: boolean;
  suspensionReason?: string | null;
  terminationTimeRemaining?: string | null;
  useQualifyingLevelPurchaseStart?: boolean;
}

function withProspectDeadlineOverride(
  person: Person,
  level: LevelMetricInput | null,
  options: ProspectDeadlineOptions = {}
): LevelMetricInput | null {
  if (!level || !isPreAssociateProspect(person)) return level;

  const prospectDeadline = getProspectAssociateProgramDeadline(person, options);
  if (!prospectDeadline) return level;

  return { ...level, expirationDate: prospectDeadline };
}

function buildQualifyingLevelDateMetrics(person: Person, options: LevelDateMetricsOptions): MetricCardType<Path>[] {
  const { isSuspended, suspensionReason, terminationTimeRemaining, useQualifyingLevelPurchaseStart } = options;
  const deadlineOptions: ProspectDeadlineOptions = { useQualifyingLevelPurchaseStart };
  const level = withProspectDeadlineOverride(person, getQualifyingLevelMetricInput(person), deadlineOptions);

  const startDate = buildStartDateMetric(level);
  const endDate = buildEndDateMetric(level, isSuspended, suspensionReason);
  const metrics: MetricCardType<Path>[] = [startDate, endDate];

  if (level?.isCompleted) {
    if (shouldShowAssociateApplicationDeadline(person, level.expirationDate)) {
      pushAssociateApplicationDeadlineMetrics(metrics, level.expirationDate);
    }
  } else {
    if (endDate.label === LEVEL_DEADLINE_LABEL && isPreAssociateProspect(person) && useQualifyingLevelPurchaseStart) {
      endDate.label = prospectDeadlineLabel(person);
    }

    const timeRemaining = buildTimeRemainingMetric(level);
    if (timeRemaining) metrics.push(timeRemaining);
  }

  const termination = buildTerminationMetric(level, isSuspended, terminationTimeRemaining);
  if (termination) metrics.push(termination);

  return metrics;
}

export function buildLevelDateMetrics(
  person: Person,
  levelNumber: number,
  options: LevelDateMetricsOptions
): MetricCardType<Path>[] {
  if (levelNumber === 0) {
    return buildQualifyingLevelDateMetrics(person, options);
  }

  const { isSuspended, suspensionReason, terminationTimeRemaining } = options;
  const level = getLevelInput(person, levelNumber);

  const startDate = buildStartDateMetric(level);
  const endDate = buildEndDateMetric(level, isSuspended, suspensionReason);
  const timeRemaining = buildTimeRemainingMetric(level);
  const termination = buildTerminationMetric(level, isSuspended, terminationTimeRemaining);

  return [startDate, endDate, ...(timeRemaining ? [timeRemaining] : []), ...(termination ? [termination] : [])];
}
